import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Sparkles, Save } from "lucide-react";
import { toast } from "sonner";

const PROVIDER_LABELS = {
  anthropic: "Anthropic (Claude)",
  openai: "OpenAI (GPT)",
  gemini: "Google (Gemini)",
};

export default function AISettings() {
  const [models, setModels] = useState({});
  const [form, setForm] = useState({ provider: "anthropic", model: "", use_custom_key: false, custom_api_key: "" });
  const [maskedKey, setMaskedKey] = useState("");
  const [hasKey, setHasKey] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, s] = await Promise.all([api.get("/ai-settings/models"), api.get("/ai-settings")]);
        setModels(m.data);
        setForm({
          provider: s.data.provider,
          model: s.data.model,
          use_custom_key: s.data.use_custom_key,
          custom_api_key: "",
        });
        setMaskedKey(s.data.custom_api_key || "");
        setHasKey(s.data.has_custom_key);
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, []);

  const providerModels = models[form.provider] || [];
  // Auto-pick first model if current model not in new provider list
  useEffect(() => {
    if (providerModels.length > 0 && !providerModels.includes(form.model)) {
      setForm((f) => ({ ...f, model: providerModels[0] }));
    }
  }, [form.provider, providerModels]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Only send new key if user typed one
      if (!payload.custom_api_key) delete payload.custom_api_key;
      const { data } = await api.put("/ai-settings", { ...form });
      setMaskedKey(data.custom_api_key || "");
      setHasKey(data.has_custom_key);
      setForm((f) => ({ ...f, custom_api_key: "" }));
      toast.success("Ajustes guardados");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Ajustes de IA"
        subtitle="Elige el proveedor y modelo que usará el generador. Por defecto se usa la clave universal Emergent; puedes sustituirla por tu propia clave."
      />

      <Card className="p-8 rounded-sm border-[#E5E7EB] shadow-none max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#7B1826] flex items-center justify-center rounded-sm">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-display text-2xl">Proveedor y modelo</h2>
            <p className="text-xs text-neutral-500">Se aplica a todas las generaciones de este usuario.</p>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wider">Proveedor</Label>
            <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="ai-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(models).length === 0 && ["anthropic", "openai", "gemini"].map((p) =>
                  <SelectItem key={p} value={p}>{PROVIDER_LABELS[p]}</SelectItem>)}
                {Object.keys(models).map((p) => <SelectItem key={p} value={p}>{PROVIDER_LABELS[p] || p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Modelo</Label>
            <Select value={form.model} onValueChange={(v) => setForm({ ...form, model: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="ai-model-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {providerModels.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="border-t border-[#E5E7EB] pt-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Usar mi propia clave API</div>
                <div className="text-xs text-neutral-500">Si está desactivado, se usa la clave universal Emergent.</div>
              </div>
              <Switch
                checked={form.use_custom_key}
                onCheckedChange={(v) => setForm({ ...form, use_custom_key: v })}
                data-testid="ai-use-custom-switch"
              />
            </div>

            {form.use_custom_key && (
              <div className="mt-4 space-y-2">
                <Label className="text-xs uppercase tracking-wider">
                  Clave API personal — proveedor seleccionado
                </Label>
                <Input
                  type="password"
                  value={form.custom_api_key}
                  onChange={(e) => setForm({ ...form, custom_api_key: e.target.value })}
                  placeholder={hasKey ? `Actualmente: ${maskedKey} — escribe aquí para cambiar` : "sk-..."}
                  data-testid="ai-custom-key-input"
                  className="rounded-sm font-mono text-xs"
                />
                <p className="text-[11px] text-neutral-500">
                  {form.provider === "anthropic" && "Tu clave de Anthropic console.anthropic.com"}
                  {form.provider === "openai" && "Tu clave de platform.openai.com"}
                  {form.provider === "gemini" && "Tu clave de aistudio.google.com"}
                </p>
              </div>
            )}
          </div>

          <Button onClick={save} disabled={saving} data-testid="ai-save-button" className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]">
            <Save className="w-4 h-4 mr-2" />{saving ? "Guardando..." : "Guardar ajustes"}
          </Button>
        </div>
      </Card>
    </PageContainer>
  );
}
